import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { useSidebarState } from '@/components/blocks/Layout/Layout.state';
import { Avatar } from '@/components/ui/Avatar';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Rail } from '@/design-system-v2/rail';
import { ChangelogMenuItem } from '@/features/changelog';
import { useSimulatorStatusQuery } from '@/features/channel-simulator';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { logoutWithMeetingGuard } from '@/features/meetings/meeting-guard';
import { useShortcutLabel } from '@/features/shortcuts';
import {
  formatResetDate,
  subscriptionStore,
  useCreditUsage,
} from '@/features/subscription';
import { useHashRoute } from '@/hooks/useHashRoute';
import { Panel, uiStore, type MainSidebarNavItem } from '@/stores/ui.store';
import { updateStore } from '@/stores/updateStore';
import { userStore } from '@/stores/user.store';
import { type Routes } from '@/types/routes';
import { trackEvent } from '@/utils/analytics';
import { handleExternalUrlClick } from '@/utils/platform';

type NavbarMenuItemBase = {
  // Any icon component that takes a className: the lucide icons plus our own
  // brand marks, which are images rather than SVG components.
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  hasAccess: boolean;
};

type NavbarMenuItem = NavbarMenuItemBase &
  ({ panel: Panel; hashRoute?: never } | { hashRoute: string; panel?: never });

const ExtrasMenuItems = observer(() => {
  const hasFeature = useFeature();
  const simulatorStatus = useSimulatorStatusQuery();
  const availableItems: NavbarMenuItem[] = [
    {
      icon: Icons.arena,
      label: 'Arena',
      panel: Panel.Arena,
      hasAccess: hasFeature(FEATURES.ARENA),
    },
    {
      icon: Icons.ingestion,
      label: 'Inputs',
      panel: Panel.Ingestion,
      hasAccess: hasFeature(FEATURES.API_REQUEST_LOGGER),
    },
    {
      icon: Icons.debug,
      label: 'CK Debug',
      panel: Panel.CkDebug,
      hasAccess: hasFeature(FEATURES.API_REQUEST_LOGGER),
    },
    {
      icon: Icons.debug,
      label: 'Meeting Debug',
      panel: Panel.MeetingDebug,
      hasAccess: hasFeature(FEATURES.API_REQUEST_LOGGER),
    },
    {
      icon: Icons.memory,
      label: 'Memory',
      panel: Panel.Echonotes,
      hasAccess: true,
    },
    {
      icon: Icons.debug,
      label: 'Request Logger',
      panel: Panel.Debug,
      hasAccess: hasFeature(FEATURES.API_REQUEST_LOGGER),
    },
    {
      icon: Icons.history,
      label: 'Summaries',
      panel: Panel.SummaryTimeline,
      hasAccess: hasFeature(FEATURES.API_REQUEST_LOGGER),
    },
    {
      icon: Icons.chartGantt,
      label: 'Activity Timeline',
      panel: Panel.ActivityTimeline,
      // Matches the route's own gate: internal access, not a flag
      hasAccess: userStore.isInternalUser,
    },
    {
      icon: Icons.mcp,
      label: 'MCP info',
      panel: Panel.McpInfo,
      hasAccess: true,
    },
    {
      icon: Icons.smartphone,
      label: 'Channel simulator',
      panel: Panel.ChannelSimulator,
      hasAccess: simulatorStatus.data?.active === true,
    },
  ].filter((item) => item.hasAccess);

  return (
    <>
      {availableItems.map((item) => (
        <MoreItem key={item.label} menuItem={item} />
      ))}
    </>
  );
});

const MoreItem = ({ menuItem }: { menuItem: NavbarMenuItem }) => {
  const { setSelectedPanel } = useSidebarState();
  const { setHash } = useHashRoute();
  const navigate = useNavigate();

  const handleClick = () => {
    if (menuItem.hashRoute) {
      setHash(menuItem.hashRoute);
    } else if (menuItem.panel) {
      setSelectedPanel(menuItem.panel);
      navigate({ to: menuItem.panel as string });
    }
  };

  return (
    <DropdownMenu.Item
      className="gap-2 whitespace-nowrap"
      onClick={handleClick}
    >
      <menuItem.icon className="text-foreground-subtle" />
      <span className="whitespace-nowrap">{menuItem.label}</span>
    </DropdownMenu.Item>
  );
};

const UsageMenuItem = observer(() => {
  const { setHash } = useHashRoute();
  const { usedPct, resetsAt, hasUsageFigure } = useCreditUsage();

  if (!hasUsageFigure || !resetsAt) {
    return (
      <DropdownMenu.Item
        className="gap-2"
        onClick={() => setHash('settings/Usage')}
      >
        <Icons.insights className="text-foreground-subtle" />
        <span>Usage</span>
      </DropdownMenu.Item>
    );
  }

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="gap-2">
        <Icons.insights className="text-foreground-subtle" />
        <span className="flex-1">Usage</span>
        <span className="text-xs text-foreground-muted tabular-nums">
          {usedPct}% used
        </span>
      </DropdownMenu.SubTrigger>
      <DropdownMenu.SubContent>
        <XStack className="items-center justify-between gap-4 px-3 py-2">
          <YStack className="min-w-0 gap-0.5">
            <span className="text-sm">Usage</span>
            <span className="text-xs text-foreground-muted">
              Resets on {formatResetDate(resetsAt)}
            </span>
          </YStack>
          <span className="shrink-0 text-sm tabular-nums">{usedPct}% used</span>
        </XStack>
        <DropdownMenu.Separator />
        <DropdownMenu.Item onClick={() => setHash('settings/Usage')}>
          <span>View usage</span>
        </DropdownMenu.Item>
      </DropdownMenu.SubContent>
    </DropdownMenu.Sub>
  );
});

const ProfileMenuItems = observer(({ onClose }: { onClose: () => void }) => {
  const { setHash } = useHashRoute();
  const user = userStore.user;
  const settingsShortcut = useShortcutLabel('app.openSettings');
  const showUsageItem = subscriptionStore.isPlus;
  const showTeamItem = subscriptionStore.isTeamMember;
  const showDownloadMobileApp = user?.is_onboarded_mobile !== true;

  return (
    <>
      <DropdownMenu.Label className="truncate">
        {user?.email}
      </DropdownMenu.Label>
      <DropdownMenu.Item
        className="gap-2"
        id="general-settings"
        onClick={() => setHash('settings/General')}
      >
        <Icons.settings className="text-foreground-subtle" />
        <span>Settings</span>
        {settingsShortcut && (
          <DropdownMenu.Shortcut>{settingsShortcut}</DropdownMenu.Shortcut>
        )}
      </DropdownMenu.Item>
      {showUsageItem && <UsageMenuItem />}
      {showTeamItem && (
        <DropdownMenu.Item
          className="gap-2"
          onClick={() => setHash('settings/Team/analytics')}
        >
          <Icons.attendees className="text-foreground-subtle" />
          <span>Team</span>
        </DropdownMenu.Item>
      )}
      {!subscriptionStore.isPlus && (
        <DropdownMenu.Item
          className="gap-2"
          onClick={() => {
            setHash('settings/Subscription');
            trackEvent('UPGRADE_PLAN_MENU_CLICKED');
            onClose();
          }}
        >
          <Icons.circleArrowUp className="text-foreground-subtle" />
          <span>Upgrade Plan</span>
        </DropdownMenu.Item>
      )}
      {showDownloadMobileApp && (
        <DropdownMenu.Item
          className="gap-2"
          onClick={() => setHash('download-mobile-app')}
        >
          <Icons.download className="text-foreground-subtle" />
          <span>Download Mobile App</span>
        </DropdownMenu.Item>
      )}
      <DropdownMenu.Separator />
      <DropdownMenu.Item className="gap-2" onClick={() => setHash('feedback')}>
        <Icons.feedback className="text-foreground-subtle" />
        <span>Give Feedback</span>
      </DropdownMenu.Item>
      <DropdownMenu.Item
        className="gap-2"
        onClick={() =>
          handleExternalUrlClick('https://littlebird.ai/help-center')
        }
      >
        <Icons.info className="text-foreground-subtle" />
        <span>Help Center</span>
      </DropdownMenu.Item>
      <ChangelogMenuItem />
      <DropdownMenu.Item
        className="group gap-2"
        onClick={() => {
          trackEvent('REFERRAL_MENU_CLICKED');
          setHash('invite');
        }}
      >
        <Icons.refer className="text-foreground-subtle origin-bottom motion-safe:group-hover:animate-gift-wiggle motion-safe:group-focus-visible:animate-gift-wiggle" />
        <span>
          {subscriptionStore.state.userReferralRewardType === 'activity_based'
            ? 'Get 1 Month Free'
            : 'Get 2 Months Free'}
        </span>
      </DropdownMenu.Item>
      <DropdownMenu.Separator />
      <DropdownMenu.Item
        className="gap-2"
        id="logout-button"
        onClick={() => logoutWithMeetingGuard()}
      >
        <Icons.logOut className="text-foreground-subtle" />
        <span>Logout</span>
      </DropdownMenu.Item>
    </>
  );
});

export type RailItem = {
  id: Exclude<MainSidebarNavItem, 'extras'>;
  label: string;
  caption?: string;
  icon: React.ReactNode;
  route?: Routes;
  enabled?: boolean;
  active?: boolean;
  onIntent?: () => void;
  onClick?: (event?: React.MouseEvent) => void;
};

export const NavRailMore = observer(
  ({
    items,
    active,
    showExtras,
    onSelect,
    onCustomize,
    onHideWelcome,
  }: {
    items: RailItem[];
    active: boolean;
    showExtras: boolean;
    onSelect: (item: RailItem, event: React.MouseEvent) => void;
    onCustomize: () => void;
    onHideWelcome?: () => void;
  }) => {
    const [open, setOpen] = useState(false);
    return (
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <Rail.Button label="More" active={open || active}>
            <Icons.moreHorizontal strokeWidth={1.75} />
          </Rail.Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          side="right"
          align="start"
          fit
          className="min-w-44"
        >
          {items.map((item) => (
            <DropdownMenu.Item
              key={item.id}
              className={MORE_MENU_ITEM}
              onPointerEnter={item.onIntent}
              onClick={(event) => onSelect(item, event)}
            >
              <MoreMenuIcon>{item.icon}</MoreMenuIcon>
              <span className="flex-1">{item.label}</span>
            </DropdownMenu.Item>
          ))}
          {showExtras && (
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger className={MORE_MENU_ITEM}>
                <MoreMenuIcon>
                  <Icons.extras />
                </MoreMenuIcon>
                <span>Extras</span>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.SubContent>
                <ExtrasMenuItems />
              </DropdownMenu.SubContent>
            </DropdownMenu.Sub>
          )}
          {(items.length > 0 || showExtras) && <DropdownMenu.Separator />}
          <DropdownMenu.Item
            className={MORE_MENU_ITEM}
            onSelect={(event) => {
              event.preventDefault();
              uiStore.setNavRailLabelsVisible(!uiStore.navRailLabelsVisible);
            }}
          >
            <MoreMenuIcon>
              <Icons.caseSensitive />
            </MoreMenuIcon>
            <span className="flex-1">Show labels</span>
            {uiStore.navRailLabelsVisible && (
              <Icons.check className="text-foreground-subtle" />
            )}
          </DropdownMenu.Item>
          {onHideWelcome && (
            <DropdownMenu.Item
              className={MORE_MENU_ITEM}
              onClick={onHideWelcome}
            >
              <MoreMenuIcon>
                <Icons.eyeOff />
              </MoreMenuIcon>
              <span>Hide Welcome</span>
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item className={MORE_MENU_ITEM} onClick={onCustomize}>
            <MoreMenuIcon>
              <Icons.slidersHorizontal />
            </MoreMenuIcon>
            <span>Customize sidebar</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);

const MORE_MENU_ITEM = 'gap-2';

const MoreMenuIcon = ({ children }: { children: React.ReactNode }) => (
  <span className="flex size-4 shrink-0 items-center justify-center text-foreground-subtle [&>svg]:size-4">
    {children}
  </span>
);

export const NavRailExtras = () => {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <Rail.Button label="Extras" active={open}>
          <Icons.extras strokeWidth={1.75} />
        </Rail.Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content side="right" align="start" className="w-fit">
        <ExtrasMenuItems />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export const NavRailUpdate = observer(() => {
  const { availableVersion, downloadProgress, isDownloading } =
    updateStore.state;

  if (updateStore.shouldShowToast) {
    return (
      <Rail.Button
        label={`Restart to update to ${availableVersion}`}
        caption="Restart"
        onClick={() => updateStore.installUpdate()}
      >
        <NavRailUpdateIcon>
          <Icons.refresh strokeWidth={2} />
        </NavRailUpdateIcon>
      </Rail.Button>
    );
  }

  if (!updateStore.isUpdateAvailable || updateStore.isUpdateReady) return null;

  if (isDownloading) {
    const percent = Math.floor(downloadProgress ?? 0);
    return (
      <Rail.Button
        label={`Downloading update to ${availableVersion} (${percent}%)`}
        caption={`${percent}%`}
        aria-disabled
        className="cursor-default"
      >
        <NavRailUpdateIcon>
          <Icons.loader strokeWidth={2} className="animate-spin" />
        </NavRailUpdateIcon>
      </Rail.Button>
    );
  }

  return (
    <Rail.Button
      label={`Download update to ${availableVersion}`}
      caption="Update"
      onClick={() => void updateStore.downloadUpdate()}
    >
      <NavRailUpdateIcon>
        <Icons.download strokeWidth={2} />
      </NavRailUpdateIcon>
    </Rail.Button>
  );
});

const NavRailUpdateIcon = ({ children }: { children: React.ReactNode }) => (
  <span className="flex size-7 items-center justify-center rounded-full bg-accent text-accent-foreground [&>svg]:size-4">
    {children}
  </span>
);

export const NavRailProfile = observer(() => {
  const [open, setOpen] = useState(false);
  const user = userStore.user;
  const title = user?.name || 'User';

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(next) => {
        if (next) trackEvent('PROFILE_MENU_CLICKED');
        setOpen(next);
      }}
    >
      <DropdownMenu.Trigger id="user-profile-trigger" asChild>
        <Rail.Button label={title} caption={false} active={open}>
          <Avatar
            avatarUrl={user?.avatar_url}
            alt="User avatar"
            fallback={title.charAt(0)}
            containerClassName="bg-surface-active size-6 shrink-0 rounded-full flex items-center justify-center overflow-hidden text-[11px] font-medium leading-none"
          />
        </Rail.Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        side="right"
        align="end"
        sideOffset={8}
        className="w-64"
      >
        <ProfileMenuItems onClose={() => setOpen(false)} />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});
